import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip as MuiChip,
  Autocomplete,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Business as CorpIcon,
  CheckCircle as AttendedIcon,
  RequestQuote as RequestFeeIcon,
  AttachMoney as FeePaidIcon,
  EventBusy as ExcusedIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Panel, Chip, Btn, Dot, PageHead, Portrait, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

// Event type colors
const EVENT_TYPE_COLORS = {
  Mining: { bg: 'rgba(255, 170, 0, 0.2)', border: '#ffaa00', text: '#ffaa00' },
  PVE: { bg: 'rgba(0, 200, 81, 0.2)', border: '#00c851', text: '#00c851' },
  PVP: { bg: 'rgba(255, 68, 68, 0.2)', border: '#ff4444', text: '#ff4444' },
  Meeting: { bg: 'rgba(51, 181, 229, 0.2)', border: '#33b5e5', text: '#33b5e5' },
  Other: { bg: 'rgba(155, 164, 176, 0.2)', border: '#9ba4b0', text: '#9ba4b0' }
};

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Format ISK number
const formatISK = (amount) => {
  if (amount >= 1000000000) {
    return (amount / 1000000000).toFixed(2) + 'B ISK';
  } else if (amount >= 1000000) {
    return (amount / 1000000).toFixed(2) + 'M ISK';
  } else if (amount >= 1000) {
    return (amount / 1000).toFixed(2) + 'K ISK';
  }
  return amount.toLocaleString() + ' ISK';
};

function EventPlanner() {
  const navigate = useNavigate();
  const { user } = useOutletContext();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [mains, setMains] = useState([]);
  const [activeMains, setActiveMains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user can manage events (requires canViewMembers permission)
  const canManageEvents = user?.permissions?.canViewMembers;
  // Alias for attendance management (same permission)
  const canManageAttendance = canManageEvents;

  // Dialog state for create/edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'Other',
    description: '',
    time: '19:00',
    participants: [],
    corpSupport: false,
    noAttendanceFee: 0,
    feeType: 'per_main'
  });

  // Selected event for details panel (replaces view dialog)
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Notification state
  const [notifying, setNotifying] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);

  // Notification logs modal state
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [eventLogs, setEventLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Fetch events for the current month
  const fetchEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);

      const response = await axios.get(`${API_BASE_URL}/events`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      setEvents(response.data);
    } catch (err) {
      console.error('Error fetching events:', err);
      if (err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch events');
    }
  };

  // Fetch main characters for participant selection
  const fetchMains = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/events/mains`);
      setMains(response.data);
    } catch (err) {
      console.error('Error fetching mains:', err);
    }
  };

  // Fetch active main characters for attendance tracking
  const fetchActiveMains = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/events/active-mains`);
      setActiveMains(response.data);
    } catch (err) {
      console.error('Error fetching active mains:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEvents(), fetchMains(), fetchActiveMains()]);
      setLoading(false);
    };
    loadData();
  }, [currentDate]);

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust to start week on Monday (0 = Monday, 6 = Sunday)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Next month days to fill the grid
    const remaining = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentDate]);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate.getFullYear() === date.getFullYear() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getDate() === date.getDate()
      );
    });
  };

  // Navigate months
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle day click - open create dialog (only if user has permission)
  const handleDayClick = (date) => {
    if (!canManageEvents) return; // Don't open create dialog without permission

    setSelectedDate(date);
    setEditingEvent(null);
    setFormData({
      name: '',
      type: 'Other',
      description: '',
      time: '19:00',
      participants: [],
      corpSupport: false,
      noAttendanceFee: 0,
      feeType: 'per_main'
    });
    setDialogOpen(true);
  };

  // Handle event click - show in details panel
  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
  };

  // Handle edit event - open dialog from details panel
  const handleEditEvent = () => {
    setEditingEvent(selectedEvent);
    setSelectedDate(new Date(selectedEvent.date));
    setFormData({
      name: selectedEvent.name,
      type: selectedEvent.type,
      description: selectedEvent.description || '',
      time: selectedEvent.time,
      participants: selectedEvent.participants || [],
      corpSupport: selectedEvent.corpSupport || false,
      noAttendanceFee: selectedEvent.noAttendanceFee || 0,
      feeType: selectedEvent.feeType || 'per_main'
    });
    setDialogOpen(true);
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/events/${selectedEvent._id}`);
      setEvents(events.filter(e => e._id !== selectedEvent._id));
      setSelectedEvent(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.response?.data?.error || 'Failed to delete event');
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const eventData = {
        ...formData,
        date: selectedDate.toISOString()
      };

      if (editingEvent) {
        const response = await axios.put(`${API_BASE_URL}/events/${editingEvent._id}`, eventData);
        setEvents(events.map(e => e._id === editingEvent._id ? response.data : e));
        // Update selected event if it was the one being edited
        if (selectedEvent && selectedEvent._id === editingEvent._id) {
          setSelectedEvent(response.data);
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}/events`, eventData);
        setEvents([...events, response.data]);
        // Select the newly created event
        setSelectedEvent(response.data);
      }

      setDialogOpen(false);
      setEditingEvent(null);
      setSelectedDate(null);
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.response?.data?.error || 'Failed to save event');
    }
  };

  // Handle attendance update
  const handleAttendanceChange = async (characterId, characterName, newStatus) => {
    if (!selectedEvent || !canManageAttendance) return;

    try {
      const response = await axios.put(`${API_BASE_URL}/events/${selectedEvent._id}/attendance`, {
        characterId,
        characterName,
        status: newStatus
      });

      // Update events list and selected event
      setEvents(events.map(e => e._id === selectedEvent._id ? response.data : e));
      setSelectedEvent(response.data);
    } catch (err) {
      console.error('Error updating attendance:', err);
      setError(err.response?.data?.error || 'Failed to update attendance');
    }
  };

  // Get attendance status for a character
  const getAttendanceStatus = (characterId) => {
    if (!selectedEvent?.attendance) return null;
    const attendance = selectedEvent.attendance.find(a => a.characterId === characterId);
    return attendance?.status || null;
  };

  // Get fee code for a character (only shown for request_fee status)
  const getFeeCode = (characterId) => {
    if (!selectedEvent?.attendance) return null;
    const attendance = selectedEvent.attendance.find(a => a.characterId === characterId);
    return attendance?.feeCode || null;
  };

  // Get received amount for a character
  const getReceivedAmount = (characterId) => {
    if (!selectedEvent?.attendance) return 0;
    const attendance = selectedEvent.attendance.find(a => a.characterId === characterId);
    return attendance?.receivedAmount || 0;
  };

  // Calculate expected fee for a character based on feeType
  // If status is 'fee_paid' and expectedFee is persisted, use that locked value
  const getExpectedFee = (characterId) => {
    if (!selectedEvent) return 0;

    // Check if there's a persisted expectedFee (locked when status changed to fee_paid)
    const attendance = selectedEvent.attendance?.find(a => a.characterId === characterId);
    if (attendance?.expectedFee !== null && attendance?.expectedFee !== undefined) {
      return attendance.expectedFee;
    }

    // Otherwise, calculate dynamically
    const baseFee = selectedEvent.noAttendanceFee || 0;
    if (selectedEvent.feeType === 'per_account') {
      const main = activeMains.find(m => m.characterId === characterId);
      return baseFee * (main?.numAccounts || 1);
    }
    return baseFee;
  };

  // Calculate open amount for a character
  // If status is 'fee_paid', open amount is always 0
  // If status is 'request_fee', open amount is expected - received
  const getOpenAmount = (characterId) => {
    const status = getAttendanceStatus(characterId);

    // Fee paid means no open amount
    if (status === 'fee_paid') {
      return 0;
    }

    // For request_fee, calculate expected - received
    const expected = getExpectedFee(characterId);
    const received = getReceivedAmount(characterId);
    return expected - received;
  };

  // Send event notification
  const handleSendEventNotification = async () => {
    if (!selectedEvent || !canManageAttendance) return;

    setNotifying(true);
    setNotificationResult(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/notifications/event/${selectedEvent._id}`, {
        notificationType: 'new_event'
      });
      setNotificationResult({
        type: 'success',
        message: `Notification sent to ${response.data.sent} member(s)`
      });
    } catch (err) {
      console.error('Error sending notification:', err);
      setNotificationResult({
        type: 'error',
        message: err.response?.data?.error || 'Failed to send notification'
      });
    } finally {
      setNotifying(false);
    }
  };

  // Send fee reminders
  const handleSendFeeReminders = async () => {
    if (!selectedEvent || !canManageAttendance || !selectedEvent.corpSupport) return;

    setNotifying(true);
    setNotificationResult(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/notifications/fee-reminders/${selectedEvent._id}`);
      setNotificationResult({
        type: 'success',
        message: `Fee reminders sent to ${response.data.sent} member(s)`
      });

      // Refresh the event to get the newly generated fee codes
      const eventResponse = await axios.get(`${API_BASE_URL}/events/${selectedEvent._id}`);
      setEvents(events.map(e => e._id === selectedEvent._id ? eventResponse.data : e));
      setSelectedEvent(eventResponse.data);
    } catch (err) {
      console.error('Error sending fee reminders:', err);
      setNotificationResult({
        type: 'error',
        message: err.response?.data?.error || 'Failed to send fee reminders'
      });
    } finally {
      setNotifying(false);
    }
  };

  // Fetch notification logs for the selected event
  const handleOpenLogsModal = async () => {
    if (!selectedEvent || !canManageAttendance) return;

    setLogsModalOpen(true);
    setLogsLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/notifications/logs`, {
        params: { eventId: selectedEvent._id, limit: 50 }
      });
      setEventLogs(response.data);
    } catch (err) {
      console.error('Error fetching notification logs:', err);
      setEventLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // Format log date
  const formatLogDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get log type label
  const getLogTypeLabel = (type) => {
    const labels = {
      'event_notification': 'Event Notification',
      'fee_reminder': 'Fee Reminder',
      'announcement': 'Announcement'
    };
    return labels[type] || type;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Map event type to cal-event CSS modifier class
  const typeClass = (type) => {
    switch (type) {
      case 'Mining':  return 'mining';
      case 'PVP':     return 'pvp';
      case 'PVE':     return 'pve';
      case 'Meeting': return 'meet';
      default:        return '';
    }
  };

  // Map event type to HUD chip tone
  const typeTone = (type) => {
    switch (type) {
      case 'Mining':  return 'amber';
      case 'PVP':     return 'danger';
      case 'PVE':     return 'ok';
      case 'Meeting': return 'magenta';
      default:        return 'ghost';
    }
  };

  const monthLabel = `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  if (loading) {
    return (
      <>
        <PageHead crumb="COMMAND // SCHEDULE" title="Event Planner" />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={48} />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Page Header */}
      <PageHead
        crumb="COMMAND // SCHEDULE"
        title="Event Planner"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Btn onClick={goToToday}>{I.Event} Today</Btn>
            {canManageEvents && (
              <Btn
                tone="primary"
                icon={I.Plus}
                onClick={() => {
                  setSelectedDate(new Date());
                  setEditingEvent(null);
                  setFormData({ name: '', type: 'Other', description: '', time: '19:00', participants: [], corpSupport: false, noAttendanceFee: 0, feeType: 'per_main' });
                  setDialogOpen(true);
                }}
              >
                New Event
              </Btn>
            )}
          </div>
        }
      />

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Calendar Panel */}
      <Panel
        title="Calendar"
        sub={monthLabel}
        action={
          <div className="cal-nav">
            <span className="cal-arrow" onClick={goToPrevMonth}>‹</span>
            <span className="mono" style={{ fontSize: 13 }}>{monthLabel}</span>
            <span className="cal-arrow" onClick={goToNextMonth}>›</span>
          </div>
        }
      >
        <div className="cal-grid">
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(d => (
            <div key={d} className="cal-dayhead">{d}</div>
          ))}
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDate(day.date);
            const isTodayDate = isToday(day.date);
            const cellClass = [
              'cal-cell',
              !day.isCurrentMonth ? 'muted' : '',
              isTodayDate ? 'today' : ''
            ].filter(Boolean).join(' ');

            return (
              <div
                key={index}
                className={cellClass}
                onClick={() => handleDayClick(day.date)}
                style={{ cursor: canManageEvents ? 'pointer' : 'default' }}
              >
                <div className="day-num">
                  <span>{day.date.getDate()}</span>
                  {isTodayDate && (
                    <span className="mono" style={{ fontSize: 9, color: 'var(--accent)', marginLeft: 4 }}>TODAY</span>
                  )}
                </div>
                {dayEvents.slice(0, 3).map(ev => (
                  <div
                    key={ev._id}
                    className={`cal-event ${typeClass(ev.type)}${selectedEvent?._id === ev._id ? ' active' : ''}`}
                    onClick={e => handleEventClick(ev, e)}
                    title={`${ev.time} — ${ev.name}`}
                  >
                    {ev.time} {ev.name}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: 10, color: 'var(--text-3)', paddingLeft: 2 }}>
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <Chip tone="amber">Mining</Chip>
          <Chip tone="ok">PvE</Chip>
          <Chip tone="danger">PvP</Chip>
          <Chip tone="magenta">Meeting</Chip>
          <Chip tone="ghost">Other</Chip>
        </div>
      </Panel>

      {/* Event Details Panel */}
      {selectedEvent && (
        <Panel
          title={selectedEvent.name}
          sub={<Chip tone={typeTone(selectedEvent.type)}>{selectedEvent.type}</Chip>}
          action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canManageAttendance && (
                <>
                  <Btn
                    icon={notifying ? <CircularProgress size={12} /> : I.Bell}
                    onClick={handleSendEventNotification}
                    disabled={notifying}
                    title="Send event notification"
                  >
                    Notify
                  </Btn>
                  {selectedEvent.corpSupport && (
                    <Btn
                      tone="amber"
                      icon={notifying ? <CircularProgress size={12} /> : I.Bell}
                      onClick={handleSendFeeReminders}
                      disabled={notifying}
                      title="Send fee reminders"
                    >
                      Fee Reminders
                    </Btn>
                  )}
                  <Btn icon={I.Logs} onClick={handleOpenLogsModal} title="Notification logs">Logs</Btn>
                </>
              )}
              {canManageEvents && (
                <>
                  <Btn icon={I.Event} onClick={handleEditEvent} title="Edit event">Edit</Btn>
                  <Btn tone="danger" onClick={handleDeleteEvent} title="Delete event">Delete</Btn>
                </>
              )}
              <Btn onClick={() => setSelectedEvent(null)} title="Close">✕</Btn>
            </div>
          }
          style={{ marginTop: 16 }}
        >
          {notificationResult && (
            <Alert
              severity={notificationResult.type}
              onClose={() => setNotificationResult(null)}
              sx={{ mb: 2 }}
            >
              {notificationResult.message}
            </Alert>
          )}

          <div className="grid-2" style={{ gap: 24 }}>
            {/* Left column: meta */}
            <div>
              <div style={{ marginBottom: 12 }}>
                <div className="label">Date &amp; Time</div>
                <div className="mono" style={{ fontSize: 13 }}>
                  {formatDate(selectedEvent.date)} · {selectedEvent.time} EVE
                </div>
              </div>

              {selectedEvent.description && (
                <div style={{ marginBottom: 12 }}>
                  <div className="label">Description</div>
                  <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.5 }}>
                    {selectedEvent.description}
                  </div>
                </div>
              )}

              {selectedEvent.corpSupport && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(51,181,229,0.07)', border: '1px solid var(--accent-soft)', borderRadius: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Dot tone="ok" />
                    <span style={{ color: 'var(--accent)', fontSize: 12, fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>
                      CORP SUPPORTED
                    </span>
                  </div>
                  {selectedEvent.noAttendanceFee > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-1)' }}>
                      No-attendance fee: <span className="mono" style={{ color: 'var(--amber)' }}>{formatISK(selectedEvent.noAttendanceFee)}</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="label">Created by</div>
                <div style={{ fontSize: 13, color: 'var(--text-1)' }}>{selectedEvent.createdBy?.characterName}</div>
              </div>
            </div>

            {/* Right column: participants */}
            <div>
              <div className="label" style={{ marginBottom: 8 }}>
                Participants ({selectedEvent.participants?.length || 0})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedEvent.participants?.length > 0 ? (
                  selectedEvent.participants.map(p => (
                    <Chip key={p.characterId} tone="ghost">{p.characterName}</Chip>
                  ))
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No participants yet</span>
                )}
              </div>
            </div>
          </div>

          {/* Attendance Matrix — Corp Support events only */}
          {selectedEvent.corpSupport && (
            <div style={{ marginTop: 24 }}>
              <div className="divlabel">
                Attendance Tracking
                {!canManageAttendance && (
                  <Chip tone="amber" style={{ marginLeft: 8 }}>View Only</Chip>
                )}
              </div>

              {activeMains.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No active main characters found. Mark members as "Main" and ensure they are active in the Members page.
                </Alert>
              ) : (
                <table className="hud-table" style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>Character</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      {canManageAttendance && <th style={{ textAlign: 'center' }}>Fee Code</th>}
                      {canManageAttendance && <th style={{ textAlign: 'right' }}>Open Amount</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {activeMains.map(main => {
                      const currentStatus = getAttendanceStatus(main.characterId);
                      const feeCode = getFeeCode(main.characterId);
                      return (
                        <tr key={main.characterId}>
                          <td>
                            <div className="cell-with-portrait">
                              <Portrait
                                name={main.characterName}
                                size={28}
                                src={`https://images.evetech.net/characters/${main.characterId}/portrait?size=32`}
                              />
                              <span>{main.characterName}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <ToggleButtonGroup
                              value={currentStatus}
                              exclusive
                              onChange={(e, newStatus) => handleAttendanceChange(main.characterId, main.characterName, newStatus)}
                              size="small"
                              disabled={!canManageAttendance}
                            >
                              <ToggleButton
                                value="attended"
                                sx={{ '&.Mui-selected': { backgroundColor: 'success.main', color: 'white', '&:hover': { backgroundColor: 'success.dark' } } }}
                              >
                                <Tooltip title="Attended"><AttendedIcon fontSize="small" /></Tooltip>
                              </ToggleButton>
                              <ToggleButton
                                value="request_fee"
                                sx={{ '&.Mui-selected': { backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' } } }}
                              >
                                <Tooltip title="Request Fee"><RequestFeeIcon fontSize="small" /></Tooltip>
                              </ToggleButton>
                              <ToggleButton
                                value="fee_paid"
                                sx={{ '&.Mui-selected': { backgroundColor: 'warning.main', color: 'white', '&:hover': { backgroundColor: 'warning.dark' } } }}
                              >
                                <Tooltip title="Fee Paid"><FeePaidIcon fontSize="small" /></Tooltip>
                              </ToggleButton>
                              <ToggleButton
                                value="excused"
                                sx={{ '&.Mui-selected': { backgroundColor: 'info.main', color: 'white', '&:hover': { backgroundColor: 'info.dark' } } }}
                              >
                                <Tooltip title="Excused"><ExcusedIcon fontSize="small" /></Tooltip>
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </td>
                          {canManageAttendance && (
                            <td style={{ textAlign: 'center' }}>
                              {feeCode
                                ? <span className="mono" style={{ color: 'var(--danger)', fontSize: 12 }}>{feeCode}</span>
                                : <span style={{ color: 'var(--text-3)' }}>—</span>}
                            </td>
                          )}
                          {canManageAttendance && (
                            <td className="num">
                              {(currentStatus === 'request_fee' || currentStatus === 'fee_paid') ? (() => {
                                const openAmount = getOpenAmount(main.characterId);
                                const received = getReceivedAmount(main.characterId);
                                const expected = getExpectedFee(main.characterId);
                                return (
                                  <Tooltip title={`Expected: ${formatISK(expected)} | Received: ${formatISK(received)}`}>
                                    <span className={`mono ${openAmount <= 0 ? 'cell-amt-pos' : 'cell-amt-neg'}`} style={{ fontSize: 12 }}>
                                      {openAmount <= 0 ? 'Paid' : formatISK(openAmount)}
                                    </span>
                                  </Tooltip>
                                );
                              })() : <span style={{ color: 'var(--text-3)' }}>—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Attendance summary chips */}
              {selectedEvent.attendance && selectedEvent.attendance.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <Chip tone="ok">Attended: {selectedEvent.attendance.filter(a => a.status === 'attended').length}</Chip>
                  <Chip tone="danger">Request Fee: {selectedEvent.attendance.filter(a => a.status === 'request_fee').length}</Chip>
                  <Chip tone="amber">Fee Paid: {selectedEvent.attendance.filter(a => a.status === 'fee_paid').length}</Chip>
                  <Chip tone="ghost">Excused: {selectedEvent.attendance.filter(a => a.status === 'excused').length}</Chip>
                  <Chip tone="ghost">Not Set: {activeMains.length - selectedEvent.attendance.length}</Chip>
                </div>
              )}
            </div>
          )}
        </Panel>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Edit Event' : 'Create Event'}
          {selectedDate && (
            <span style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
              {formatDate(selectedDate)}
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
            <TextField
              label="Event Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.type}
                label="Event Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="Mining">Mining</MenuItem>
                <MenuItem value="PVE">PVE</MenuItem>
                <MenuItem value="PVP">PVP</MenuItem>
                <MenuItem value="Meeting">Meeting</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Time (EVE Time)"
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <Divider />

            {/* Corp Support Section */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.corpSupport}
                  onChange={(e) => setFormData({
                    ...formData,
                    corpSupport: e.target.checked,
                    noAttendanceFee: e.target.checked ? formData.noAttendanceFee : 0
                  })}
                  color="primary"
                />
              }
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CorpIcon fontSize="small" />
                  Corporation Supported Event
                </span>
              }
            />

            {formData.corpSupport && (
              <>
                <TextField
                  label="No Attendance Fee"
                  type="number"
                  value={formData.noAttendanceFee}
                  onChange={(e) => setFormData({ ...formData, noAttendanceFee: Number(e.target.value) || 0 })}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">ISK</InputAdornment>
                  }}
                  helperText="Fee charged to members who don't attend"
                />
                <FormControl fullWidth>
                  <InputLabel>Fee Type</InputLabel>
                  <Select
                    value={formData.feeType}
                    label="Fee Type"
                    onChange={(e) => setFormData({ ...formData, feeType: e.target.value })}
                  >
                    <MenuItem value="per_main">Per Main</MenuItem>
                    <MenuItem value="per_account">Per Account</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <Divider />

            <Autocomplete
              multiple
              options={mains}
              getOptionLabel={(option) => option.characterName}
              value={formData.participants}
              onChange={(e, newValue) => setFormData({ ...formData, participants: newValue })}
              isOptionEqualToValue={(option, value) => option.characterId === value.characterId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Participants (Main Characters)"
                  placeholder="Select participants..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <MuiChip
                    label={option.characterName}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.characterId}
                  />
                ))
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.type || !formData.time}
          >
            {editingEvent ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Logs Modal */}
      <Dialog open={logsModalOpen} onClose={() => setLogsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryIcon />
            Notification History
          </span>
          {selectedEvent && (
            <span style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
              {selectedEvent.name}
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <CircularProgress />
            </div>
          ) : eventLogs.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No notifications have been sent for this event yet.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Sent By</TableCell>
                    <TableCell align="center">Sent</TableCell>
                    <TableCell align="center">Failed</TableCell>
                    <TableCell>Recipients</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>{formatLogDate(log.createdAt)}</TableCell>
                      <TableCell>
                        <MuiChip
                          label={getLogTypeLabel(log.type)}
                          size="small"
                          color={log.type === 'fee_reminder' ? 'warning' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{log.triggeredBy?.characterName || 'System'}</TableCell>
                      <TableCell align="center">
                        <MuiChip label={log.totalSent} size="small" color="success" />
                      </TableCell>
                      <TableCell align="center">
                        {log.totalFailed > 0 ? (
                          <MuiChip label={log.totalFailed} size="small" color="error" />
                        ) : (
                          <MuiChip label="0" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 250 }}>
                          {log.recipients?.slice(0, 3).map((r, i) => (
                            <MuiChip
                              key={i}
                              label={r.characterName}
                              size="small"
                              variant="outlined"
                              color={r.success ? 'success' : 'error'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {log.recipients?.length > 3 && (
                            <MuiChip
                              label={`+${log.recipients.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EventPlanner;
